import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tiptap-editor/tiptap-ui-primitive/tooltip/tooltip';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import {
    getEditorAllowedZyVersions,
    getEditorVersionFilter,
    isZyVersionNodeVisible,
    normalizeSelectedZyVersionFilter,
} from '@/lib/zy-tiptap-utils';
import type { ReactNodeViewProps } from '@tiptap/react';
import { NodeViewContent, useEditorState } from '@tiptap/react';
import { useCallback, useMemo, useState } from 'react';
import { ZyVersionBadge } from './zyversion-badge';
import { getVersionBadgeColors, getVersionColor } from './zyversion-color';

export function ZyVersionInlineNodeView(props: ReactNodeViewProps) {
    const { node, editor, updateAttributes, decorations } = props;
    const isEditorReadonly = useIsEditorReadonly(editor);
    const selectedZyVersion =
        useEditorState({
            editor,
            selector: ({ editor: ed }) => (ed ? getEditorVersionFilter(ed) : null),
        }) ?? null;
    const version = node.attrs.version ?? '';
    const allowedVersions =
        useEditorState({
            editor,
            selector: ({ editor: ed }) => (ed ? getEditorAllowedZyVersions(ed) : []),
        }) ?? [];
    const isVisible = isZyVersionNodeVisible(selectedZyVersion, version);
    const isInlineView = normalizeSelectedZyVersionFilter(selectedZyVersion) === null;

    const stringForColorGeneration = isInlineView ? version : (normalizeSelectedZyVersionFilter(selectedZyVersion)?.join(',') ?? null);
    const badgeColors = useMemo(
        () => getVersionBadgeColors(isInlineView ? stringForColorGeneration : version),
        [stringForColorGeneration, isInlineView, version],
    );
    const backgroundColor = useMemo(
        () => getVersionColor(isInlineView ? stringForColorGeneration : version),
        [stringForColorGeneration, isInlineView, version],
    );

    const [isRevealedLocally, setIsRevealedLocally] = useState(false);
    const [isEditingVersion, setIsEditingVersion] = useState(false);
    const showContent = isVisible || isRevealedLocally || isEditingVersion;

    const handleVersionChange = useCallback(
        (v: string) => {
            updateAttributes({ version: v });
            setIsRevealedLocally(false);
        },
        [updateAttributes],
    );

    const handleLocalRevealSurfaceClick = useCallback(() => {
        setIsRevealedLocally(false);
    }, []);

    const showLocalRevealChrome = isRevealedLocally && !isVisible;

    const nodeView = (
        <ZyNodeViewWrapper
            decorations={decorations}
            as="span"
            className={`inline items-center rounded-sm px-0.5 py-0.5 ${showLocalRevealChrome && !isEditingVersion ? 'cursor-pointer' : ''}`}
            style={showContent ? { backgroundColor: backgroundColor } : undefined}
            onClick={showLocalRevealChrome && !isEditingVersion ? handleLocalRevealSurfaceClick : undefined}
        >
            {!showContent && (
                <Tooltip
                    delay={200}
                    closeDelay={0}
                >
                    <TooltipTrigger asChild>
                        <span
                            contentEditable={false}
                            className="group relative -mx-0.5 inline-flex cursor-pointer items-center rounded px-1 py-0.5 align-middle transition-colors hover:bg-black/6 dark:hover:bg-white/8"
                            aria-label={version ? `Hidden zyVersion: ${version}` : 'Hidden zyVersion'}
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsRevealedLocally(true);
                            }}
                        >
                            <span
                                className="block h-[1.2em] w-0.5 shrink-0 rounded-full opacity-75 transition-opacity group-hover:opacity-100"
                                style={{ backgroundColor: badgeColors.bg }}
                                aria-hidden
                            />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent> Expand hidden {version} content</TooltipContent>
                </Tooltip>
            )}
            <span
                className={showContent ? 'inline items-center' : 'hidden'}
                aria-hidden={!showContent}
            >
                <span
                    contentEditable={false}
                    className="mr-1 inline-flex items-center gap-0.5"
                >
                    <ZyVersionBadge
                        version={version}
                        allowedVersions={allowedVersions}
                        onVersionChange={isEditorReadonly ? undefined : handleVersionChange}
                        onVersionPopoverOpenChange={isEditorReadonly ? undefined : setIsEditingVersion}
                        colors={badgeColors}
                    />
                </span>
                <NodeViewContent<'span'> as="span" />
            </span>
        </ZyNodeViewWrapper>
    );

    return showLocalRevealChrome ? (
        <Tooltip
            delay={200}
            closeDelay={0}
            open={isEditingVersion ? false : undefined}
        >
            <TooltipTrigger asChild>{nodeView}</TooltipTrigger>
            <TooltipContent>Hide content</TooltipContent>
        </Tooltip>
    ) : (
        nodeView
    );
}
